/**
 * FE-9: το σώμα του POST/PUT κεντρικού προϊόντος (OrgCatalogManager) ως καθαρή συνάρτηση —
 * docs/base_price_phase5_plan.md §3 («εγγραφή προϊόντος»), backend utils/priceWriteGuard.
 *
 * ΓΙΑΤΙ ΥΠΑΡΧΕΙ: η τιμή ζει ΜΟΝΟ στα μεγέθη (tab «Τιμοκατάλογοι»). Η φόρμα του κεντρικού
 * καταλόγου κάποτε φόρτωνε την τιμή του προϊόντος όταν άνοιγε και την ξανάστελνε μπαγιάτικη
 * στην αποθήκευση, και επέτρεπε αποθήκευση χωρίς κανένα μέγεθος (ο backend απαντά 409
 * LAST_SIZE / 400 SIZES_REQUIRED) ή τέλος με μεγέθη (το σχήμα 7700 που χρεώνει διπλά).
 *
 * Κανόνες:
 *  - κανένα πεδίο τιμής προϊόντος στο σώμα, ούτε στη δημιουργία (ο backend αγνοεί απόν/0 και
 *    απαντά 400 PRICE_IN_SIZES_ONLY σε ≠ 0)·
 *  - τέλος (fee_category 8/16): το κλειδί sizes ΔΕΝ στέλνεται· το ποσό του ζει στο state_fees·
 *  - μη τέλος: ≥1 επώνυμο μέγεθος όταν το προϊόν είναι νέο ή ΕΙΧΕ μεγέθη (όλα μετράνε, και τα
 *    μη διαθέσιμα — ίδιο μέτρο με το LAST_SIZE του backend)· ακριβώς ένα default (αν ≠ 1 →
 *    defaultSize §3.3: το πρώτο πωλήσιμο σημειωμένο, αλλιώς το πρώτο πωλήσιμο)· υπάρχον προϊόν
 *    χωρίς μεγέθη (ανωμαλία) με κενή λίστα → το κλειδί sizes ΔΕΝ στέλνεται, ώστε η επεξεργασία
 *    του να μη μπλοκάρεται (ο backend το κρατά όπως είναι — NO_SIZE_PRODUCT_KEPT)· όταν του
 *    προστεθεί μέγεθος, στέλνεται όπως κάθε μέγεθος (απόλυτη τιμή, συνήθως 0 ως τον τιμοκατάλογο).
 *
 * ΚΑΝΕΝΑ ΠΟΣΟ ΔΕΝ ΑΛΛΑΖΕΙ: η φόρμα δεν έχει πεδίο τιμής προϊόντος.
 */

import { isFeeCategory, feeModeBlocked } from './feeForm';
import { defaultSize } from './pricing';

/** Όνομα του μεγέθους «χωρίς μέγεθος» — ίδιο με το BASE_SIZE_NAME του backend (priceWriteGuard). */
export const BASE_SIZE_NAME = 'Κανονικό';

/** Γραμμή μεγέθους όπως την κρατά η φόρμα (τιμή ως κείμενο του input). */
export type OrgSizeFormRow = {
  id?: number;
  name: string;
  price_modifier: string;
  is_default: boolean;
  /** όπως αποθηκεύτηκε· λείπει σε νέα γραμμή/πρότυπο (→ ο backend το θεωρεί διαθέσιμο) */
  is_available?: boolean;
};

export type OrgProductFormLike = {
  category_id: string;
  name: string;
  description: string;
  vat: string;
  product_type: string;
  is_available: boolean;
  display_order: number;
  vat_take_away: string;
  vat_delivery: string;
  vat_dine_in: string;
  fee_category: string;
  auto_plastic_fee_count: string;
  is_weight_variable: boolean;
  is_open_price: boolean;
  barcode: string;
  sell_unit: string;
  max_discount_percent: string;
  xenios_product_id: string;
  store_ids: number[] | null;
  /** Τόνος της παλέτας για την κάρτα στο ταμείο (migration 264)· null = χωρίς· λείπει = δεν στέλνεται. */
  color?: string | null;
  sizes: OrgSizeFormRow[];
  simple_ingredients: { ingredient_name: string; extra_price: string; is_default: boolean; category: string }[];
  option_groups: {
    name: string;
    selection_type: 'single' | 'multiple';
    is_required: boolean;
    template_id?: number | null;
    choices: { name: string; price_modifier: string; is_default: boolean }[];
  }[];
};

export type OrgPayloadOptions = {
  /** επεξεργασία υπάρχοντος (όχι δημιουργία) */
  editing: boolean;
  /** πόσα μεγέθη είχε το προϊόν ΟΠΩΣ ΑΠΟΘΗΚΕΥΤΗΚΕ (όλα, και τα μη διαθέσιμα)· 0 σε νέο */
  originalSizeCount: number;
};

export type OrgPayloadError = 'atLeastOneSizeRequired';

export type OrgSizePayload = {
  id?: number;
  name: string;
  price_modifier: number;
  is_default: boolean;
  is_available?: boolean;
};

export type OrgProductPayload = Record<string, unknown> & {
  fee_category: number | null;
  sizes?: OrgSizePayload[];
};

export type OrgPayloadResult =
  | { payload: OrgProductPayload; error?: undefined }
  | { error: OrgPayloadError; payload?: undefined };

/** fee_category της φόρμας ('' | '8' | '16') ως αριθμός ή null — όπως το έστελνε η φόρμα. */
export function formFeeCategory(v: string | null | undefined): number | null {
  return v ? parseInt(v, 10) : null;
}

/** Τέλος = ο ΙΔΙΟΣ ορισμός με το ταμείο και τον backend (§3.4: fee_category != null). */
export function isFeeForm(form: Pick<OrgProductFormLike, 'fee_category'>): boolean {
  return isFeeCategory(formFeeCategory(form.fee_category));
}

/** Γραμμή με όνομα — οι κενές γραμμές της φόρμας δεν στέλνονται (όπως πριν). */
const isNamed = (s: OrgSizeFormRow): boolean => s.name.trim() !== '';

export function namedSizes(sizes: OrgSizeFormRow[]): OrgSizeFormRow[] {
  return sizes.filter(isNamed);
}

/**
 * Χρειάζεται ≥1 μέγεθος; Μόνο σε μη τέλος, και μόνο όταν το προϊόν είναι νέο ή ήδη είχε
 * μεγέθη. Ένα υπάρχον προϊόν χωρίς μεγέθη (ανωμαλία) δεν μπλοκάρεται σε αλλαγή ονόματος —
 * ο backend το επιτρέπει όσο το αίτημα δεν αγγίζει μεγέθη.
 */
export function sizeRequired(form: Pick<OrgProductFormLike, 'fee_category'>, opts: OrgPayloadOptions): boolean {
  if (isFeeForm(form)) return false;
  return !opts.editing || opts.originalSizeCount > 0;
}

/**
 * Κουμπί X του μεγέθους idx κλειδωμένο: είναι το τελευταίο επώνυμο μέγεθος προϊόντος που
 * χρειάζεται μέγεθος. Οι κενές γραμμές σβήνονται πάντα (δεν στέλνονται ούτως ή άλλως).
 */
export function sizeRemovalLocked(
  form: Pick<OrgProductFormLike, 'fee_category' | 'sizes'>,
  idx: number,
  opts: OrgPayloadOptions,
): boolean {
  const row = form.sizes[idx];
  if (!row || !isNamed(row)) return false;
  return sizeRequired(form, opts) && namedSizes(form.sizes).length <= 1;
}

/**
 * Select του τέλους κλειδωμένο: υπάρχον ΜΗ-τέλος που έχει αποθηκευμένα μεγέθη. Ο ίδιος
 * κανόνας με τη φόρμα του καταστήματος (FE-8 feeModeBlocked): ένα τέλος δεν έχει μεγέθη και
 * ο backend αρνείται (FEE_NO_SIZES) τέλος με τιμή σε μέγεθος — ο χειριστής φτιάχνει νέο
 * προϊόν-τέλος. Υπάρχον τέλος (και με παλιά μεγέθη, σχήμα 373) αλλάζει κωδικό ελεύθερα.
 */
export function feeSelectLocked(opts: OrgPayloadOptions & { originalFeeCategory: number | null | undefined }): boolean {
  return feeModeBlocked({
    isNew: !opts.editing,
    originalSizeCount: opts.originalSizeCount,
    originalFeeCategory: opts.originalFeeCategory ?? null,
  });
}

/**
 * Προσθέτει το «Κανονικό» (τιμή 0, προεπιλεγμένο) — το μέγεθος «χωρίς μέγεθος» ενός νέου
 * προϊόντος· η τιμή του μπαίνει στον τιμοκατάλογο, όπως κάθε μεγέθους. Γίνεται ΤΟ default,
 * οπότε τα υπόλοιπα χάνουν τη σήμανση (ακριβώς ένα default).
 */
export function addBaseSize(sizes: OrgSizeFormRow[]): OrgSizeFormRow[] {
  return [
    ...sizes.map(s => (s.is_default ? { ...s, is_default: false } : s)),
    { name: BASE_SIZE_NAME, price_modifier: '0', is_default: true },
  ];
}

/**
 * Ακριβώς ένα default, με τον ΙΔΙΟ κανόνα που εφαρμόζει ο backend (priceWriteGuard →
 * sizeUpsert.normalizeDefault) και ΜΟΝΟ όταν εκείνος θα τον εφάρμοζε: πλήθος default ≠ 1.
 * Ένα μόνο σημειωμένο default περνά όπως είναι, ακόμη κι αν δεν πωλείται — έτσι το
 * αποθήκευε και πριν η φόρμα, και το defaultSize το προσπερνά ούτως ή άλλως στην ανάγνωση.
 *
 * ΓΙΑΤΙ όχι «το πρώτο σημειωμένο»: από τη στιγμή που η φόρμα κρατά το is_available, ένα μη
 * διαθέσιμο μέγεθος μπορεί να είναι σημειωμένο default. Με [Α μη διαθέσιμο default 1,00 |
 * Β 1,50 | Γ default 2,00] το defaultSize (§3.3) δίνει Γ· ο παλιός κανόνας θα έσβηνε το
 * default του Γ, θα γινόταν Β και η γραμμή χωρίς μέγεθος θα έπεφτε από 2,00 σε 1,50 χωρίς
 * απόφαση. Ο κανόνας καλείται από το pricing.ts (δίδυμο του catalogPrice.js), δεν
 * ξαναγράφεται εδώ.
 */
function withSingleDefault(rows: OrgSizeFormRow[]): OrgSizeFormRow[] {
  if (rows.filter(s => s.is_default === true).length === 1) return rows;
  // id/display_order = θέση: η σειρά της φόρμας είναι η σειρά που θα γράψει ο backend
  // (display_order = θέση), και οι νέες γραμμές δεν έχουν id.
  const keyed = rows.map((s, i) => ({ id: i, display_order: i, is_default: s.is_default, is_available: s.is_available }));
  const pick = (defaultSize(keyed) ?? keyed[0]).id;
  return rows.map((s, i) => ({ ...s, is_default: i === pick }));
}

function sizePayload(s: OrgSizeFormRow): OrgSizePayload {
  const out: OrgSizePayload = {
    id: s.id,
    name: s.name.trim(),
    price_modifier: parseFloat(s.price_modifier) || 0,
    is_default: s.is_default,
  };
  // Η διαθεσιμότητα του μεγέθους ταξιδεύει όπως αποθηκεύτηκε: ο backend γράφει
  // is_available = (s.is_available !== false), οπότε χωρίς αυτό κάθε αποθήκευση της φόρμας
  // ξανάνοιγε σιωπηλά ένα μη διαθέσιμο μέγεθος.
  if (s.is_available !== undefined) out.is_available = s.is_available;
  return out;
}

export function buildOrgProductPayload(form: OrgProductFormLike, opts: OrgPayloadOptions): OrgPayloadResult {
  const feeCategory = formFeeCategory(form.fee_category);
  const isFee = isFeeCategory(feeCategory);

  const payload: OrgProductPayload = {
    category_id: parseInt(form.category_id, 10),
    name: form.name.trim(),
    description: form.description || null,
    vat: parseFloat(form.vat),
    product_type: form.product_type,
    is_available: form.is_available,
    display_order: form.display_order,
    vat_take_away: form.vat_take_away ? parseFloat(form.vat_take_away) : null,
    vat_delivery: form.vat_delivery ? parseFloat(form.vat_delivery) : null,
    vat_dine_in: form.vat_dine_in ? parseFloat(form.vat_dine_in) : null,
    fee_category: feeCategory,
    auto_plastic_fee_count: parseInt(form.auto_plastic_fee_count, 10) || 0,
    is_weight_variable: form.is_weight_variable,
    is_open_price: form.is_open_price,
    barcode: form.barcode?.trim() || null,
    sell_unit: form.sell_unit?.trim() || null,
    max_discount_percent: form.max_discount_percent ? parseFloat(form.max_discount_percent) : null,
    xenios_product_id: form.xenios_product_id?.trim() || null,
    // null = all stores; array = only those. Always sent so availability edits persist.
    store_ids: form.store_ids && form.store_ids.length ? form.store_ids : null,
    // Χρώμα: μόνο όταν η φόρμα το έχει (null = «Χωρίς», το καθαρίζει)· ο server ελέγχει την παλέτα.
    ...(form.color !== undefined ? { color: form.color || null } : {}),
    simple_ingredients: form.simple_ingredients.filter(i => i.ingredient_name).map(i => ({
      ingredient_name: i.ingredient_name,
      extra_price: parseFloat(i.extra_price) || 0,
      is_default: i.is_default,
      category: i.category || null,
    })),
    option_groups: form.option_groups
      .filter(g => g.name.trim() && g.choices.some(c => c.name.trim()))
      .map(g => ({
        name: g.name.trim(),
        selection_type: g.selection_type,
        is_required: g.is_required,
        template_id: g.template_id ?? null,
        choices: g.choices.filter(c => c.name.trim()).map(c => ({
          name: c.name.trim(),
          price_modifier: parseFloat(c.price_modifier) || 0,
          is_default: c.is_default,
        })),
      })),
  };

  // Τέλος: χωρίς κλειδί sizes. Σε νέο, τα μεγέθη της φόρμας (π.χ. ένα «Κανονικό» πριν την
  // επιλογή τέλους) δεν έχουν αποθηκευτεί και απλώς δεν στέλνονται· σε υπάρχον τέλος με
  // παλιά μεγέθη μένουν ανέγγιχτα.
  if (isFee) return { payload };

  const named = namedSizes(form.sizes);
  if (named.length === 0) {
    if (sizeRequired(form, opts)) return { error: 'atLeastOneSizeRequired' };
    // Υπάρχον προϊόν χωρίς μεγέθη που μένει χωρίς: τίποτα για τα μεγέθη.
    return { payload };
  }
  payload.sizes = withSingleDefault(named).map(sizePayload);
  return { payload };
}
